import React, { useState } from 'react';
import { Alert } from 'react-native';
import { View, Text, Pressable, Platform, Image } from 'react-native';
import PdfIcon from '../assets/svg/ic_doc_pdf.svg';
import WordIcon from '../assets/svg/ic_doc_word.svg';
import ExcelIcon from '../assets/svg/ic_doc_xls.svg';
import PptIcon from '../assets/svg/ic_doc_ppt.svg';
import TxtIcon from '../assets/svg/ic_doc_txt.svg';
import UnknowIcon from '../assets/svg/ic_doc_unknow.svg';
import FileIcon from '../assets/svg/ic_file.svg';

export interface FileItem {
    name: string;
    size: number;
    ext: string;
    url: string;
    width?: number; // 视频/图片宽度
    height?: number; // 视频/图片高度
    progress?: number; // 0-1
    status?: 'uploading' | 'done' | 'error' | 'downloading';
    mimeType?: string; // 文件的MIME类型，可选
    thumbnailPath?: string; // 视频缩略图路径，可选
}

export interface FilePreviewListProps {
    files: FileItem[];
    onRemoveFile: (index: number) => void;
    onDownloadFile: (index: number) => void;
    onOpenFile: (index: number) => void;
}

function getIconByExt(ext: string) {
    switch (ext) {
        case 'pdf':
            return <PdfIcon width={32} height={32} />;
        case 'doc':
        case 'docx':
            return <WordIcon width={32} height={32} />;
        case 'xls':
        case 'xlsx':
            return <ExcelIcon width={32} height={32} />;
        case 'ppt':
        case 'pptx':
            return <PptIcon width={32} height={32} />;
        case 'txt':
            return <TxtIcon width={32} height={32} />;
        default:
            return <UnknowIcon width={32} height={32} />;
    }
}

function formatSize(size: number) {
    if (size < 1024) return `${size}B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
    return `${(size / 1024 / 1024).toFixed(1)}MB`;
}

export const FilePreviewList = (props: FilePreviewListProps) => {
    // 预览行为：pdf/txt用window.open，其他类型用blob url尝试浏览器预览
    const handlePreview = (file: FileItem, idx?: number) => {
        // 只允许图片/视频 window.open，文档类型一律只弹提示
        const isImage = file.mimeType?.startsWith('image/');
        const isVideo = file.mimeType?.startsWith('video/');
        if (isImage || isVideo) {
            // 允许图片/视频 window.open 预览
            try {
                window.open(file.url, '_blank');
            } catch (e) {
                // 兜底提示
                const alertMsg = '文件无法在浏览器中打开，请在本地文件管理器中用对应应用打开。';
                if (typeof Alert !== 'undefined') {
                    Alert.alert('无法预览', alertMsg, [
                        { text: '去文件位置', onPress: () => props.onOpenFile && idx !== undefined && props.onOpenFile(idx) }
                    ]);
                } else {
                    if (window.confirm(alertMsg + '\n\n点击“确定”前往文件位置')) {
                        props.onOpenFile && idx !== undefined && props.onOpenFile(idx);
                    }
                }
            }
            return;
        }
        // 文档类型一律只弹提示，不尝试 window.open
        const alertMsg = '该文件类型无法在浏览器直接预览，请在本地文件管理器中用对应应用打开。';
        if (typeof Alert !== 'undefined') {
            Alert.alert('无法直接预览', alertMsg, [
                { text: '去文件位置', onPress: () => props.onOpenFile && idx !== undefined && props.onOpenFile(idx) }
            ]);
        } else {
            if (window.confirm(alertMsg + '\n\n点击“确定”前往文件位置')) {
                props.onOpenFile && idx !== undefined && props.onOpenFile(idx);
            }
        }
    };
    return (
        <View
            style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingTop: 4, paddingHorizontal: 12 }}
        >
            {props.files.map((file, idx) => {
                // 判断是否为图片或视频
                const isImage = file.mimeType?.startsWith('image/');
                const isVideo = file.mimeType?.startsWith('video/');
                if (isImage) {
                    // 图片直接展示缩略图
                    return (
                        <View key={file.url || (file.name + '-' + file.size + '-' + file.ext + '-' + idx)} data-file-idx={idx} style={{ position: 'relative', width: 80, height: 80, borderRadius: 8, marginRight: 8, marginBottom: 4, overflow: 'hidden', backgroundColor: '#f7f7f7', borderWidth: 1, borderColor: '#e0e0e0' }}>
                            <Pressable
                                style={{ width: '100%', height: '100%' }}
                                onPress={() => handlePreview(file, idx)}
                            >
                                <Image
                                    source={{ uri: file.url }}
                                    resizeMode="cover"
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </Pressable>
                            <Pressable
                                onPress={() => props.onRemoveFile(idx)}
                                style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    width: 20,
                                    height: 20,
                                    borderRadius: 10,
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 10,
                                }}
                            >
                                <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>×</Text>
                            </Pressable>
                        </View>
                    );
                } else if (isVideo) {
                    // 视频展示缩略图+播放icon
                    return (
                        <View key={file.url || (file.name + '-' + file.size + '-' + file.ext + '-' + idx)} data-file-idx={idx} style={{ position: 'relative', width: 80, height: 80, borderRadius: 8, marginRight: 8, marginBottom: 4, overflow: 'hidden', backgroundColor: '#f7f7f7', borderWidth: 1, borderColor: '#e0e0e0' }}>
                            <Pressable
                                style={{ width: '100%', height: '100%' }}
                                onPress={() => handlePreview(file, idx)}
                            >
                                <Image
                                    source={{ uri: file.thumbnailPath || file.url }}
                                    resizeMode="cover"
                                    style={{ width: '100%', height: '100%' }}
                                />
                                <View style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    width: '100%',
                                    height: '100%',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.15)',
                                }}>
                                    <Text style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>▶</Text>
                                </View>
                            </Pressable>
                            <Pressable
                                onPress={() => props.onRemoveFile(idx)}
                                style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    width: 20,
                                    height: 20,
                                    borderRadius: 10,
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 10,
                                }}
                            >
                                <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>×</Text>
                            </Pressable>
                        </View>
                    );
                } else {
                    // 文档类型，显示icon+名字+大小
                    return (
                        <View key={file.url || (file.name + '-' + file.size + '-' + file.ext + '-' + idx)} data-file-idx={idx} style={{ position: 'relative', width: 80, height: 80, backgroundColor: '#f7f7f7', borderRadius: 8, marginRight: 8, marginBottom: 4, justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                            <Pressable
                                onPress={() => handlePreview(file, idx)}
                                style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}
                            >
                                <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%' }}>
                                    {getIconByExt(file.ext)}
                                    <Text style={{ fontSize: 10, color: '#888', marginTop: 2, maxWidth: 64, textAlign: 'center' }} numberOfLines={1} ellipsizeMode="middle">{formatSize(file.size)}</Text>
                                    <Text
                                        style={{ fontSize: 11, color: '#222', marginTop: 2, maxWidth: 64, textAlign: 'center' }}
                                        numberOfLines={2}
                                        ellipsizeMode="middle"
                                    >
                                        {file.name}
                                    </Text>
                                </View>
                            </Pressable>
                            <Pressable
                                onPress={() => props.onRemoveFile(idx)}
                                style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    width: 20,
                                    height: 20,
                                    borderRadius: 10,
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 10,
                                }}
                            >
                                <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>×</Text>
                            </Pressable>
                        </View>
                    );
                }
            })}
        </View>
    );
};
